import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ClipboardList,
  ArrowLeft,
  ArrowRight,
  Save,
  CheckCircle,
  Upload,
  Check,
} from "lucide-react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type { IEmployeeDetails, IDocumentItem, IWeightConfig } from "../types/quarters.types";

const ApplicationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { applicationId } = useParams();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const initialQuarterType = location.state?.quarterType || searchParams.get("quarterType") || "Type II";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [weights, setWeights] = useState<IWeightConfig[]>([]);

  // Form State
  const [quarterType, setQuarterType] = useState(initialQuarterType);
  const [employeeDetails, setEmployeeDetails] = useState<IEmployeeDetails>({
    employeeId: "",
    department: "",
    designation: "",
    cadre: "Faculty",
    employmentType: "Permanent",
    joiningDate: "",
    instituteJoiningDate: "",
    retirementDate: "",
    dob: "",
    age: 0,
    gender: "Male",
    maritalStatus: "Single",
    dependentsCount: 0,
    childrenCount: 0,
    isPwd: false,
    medicalConditions: "",
    currentResidence: "",
    currentQuarter: "",
    previousAllotment: "",
    disciplinaryStatus: "None",
    emergencyCategory: "None",
    serviceYears: 0,
    customFields: {
      category: "UR",
      isWidow: false,
      singleParent: false,
    },
  });

  const [documents, setDocuments] = useState<IDocumentItem[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  // Load existing application if editing
  const loadExistingApplication = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      const res = await quartersService.getApplication(applicationId);
      if (res.success && res.data) {
        const app = res.data;
        setQuarterType(app.quarterType);
        setEmployeeDetails({
          ...app.employeeDetails,
          joiningDate: app.employeeDetails.joiningDate ? app.employeeDetails.joiningDate.split("T")[0] : "",
          instituteJoiningDate: app.employeeDetails.instituteJoiningDate ? app.employeeDetails.instituteJoiningDate.split("T")[0] : "",
          retirementDate: app.employeeDetails.retirementDate ? app.employeeDetails.retirementDate.split("T")[0] : "",
          dob: app.employeeDetails.dob ? app.employeeDetails.dob.split("T")[0] : "",
        });
        setDocuments(app.documents || []);
      }
    } catch (err: any) {
      toast.error("Failed to load draft application");
    } finally {
      setLoading(false);
    }
  }, [applicationId, quartersService, toast]);

  const loadWeights = useCallback(async () => {
    try {
      const res = await quartersService.getWeights();
      if (res.success) setWeights(res.data);
    } catch (err) {}
  }, [quartersService]);

  useEffect(() => {
    loadWeights();
    loadExistingApplication();
  }, [loadExistingApplication, loadWeights]);

  // Handle Date/Age calculations
  const handleDobChange = (dobString: string) => {
    const age = dobString
      ? Math.max(0, Math.floor((new Date().getTime() - new Date(dobString).getTime()) / (365.25 * 24 * 60 * 60 * 1000)))
      : "";
    setEmployeeDetails({ ...employeeDetails, dob: dobString, age });
  };

  const handleJoiningDateChange = (joiningString: string) => {
    const serviceYears = joiningString
      ? Math.max(0, Math.floor((new Date().getTime() - new Date(joiningString).getTime()) / (365.25 * 24 * 60 * 60 * 1000)))
      : 0;
    setEmployeeDetails({ ...employeeDetails, joiningDate: joiningString, serviceYears });
  };

  // Pre-signed S3 upload implementation
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, docName: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(docName);
    try {
      const fileExtension = file.name.split(".").pop();
      const payload = {
        fileExtension,
        contentType: file.type,
        folder: "quarters",
      };

      // 1. Generate pre-signed URL
      const generateRes = await apiRequest(`/uploads/generate-upload-url/quarters-doc/${docName}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!generateRes.key || !generateRes.uploadUrl) {
        throw new Error("Failed to get upload signature");
      }

      // 2. Put file directly to S3
      const uploadContentType = generateRes.contentType || file.type || "application/octet-stream";
      const s3Response = await fetch(generateRes.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": uploadContentType,
        },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error("S3 Upload failed. Please try again.");
      }

      // 3. Confirm upload
      const confirmRes = await apiRequest(`/uploads/confirm-upload/quarters-doc/${docName}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: generateRes.key }),
      });

      if (confirmRes.url) {
        // Remove existing doc of same name
        setDocuments((prev) => [
          ...prev.filter((d) => d.name !== docName),
          { name: docName, url: confirmRes.url, key: generateRes.key },
        ]);
        toast.success(`${docName} uploaded successfully!`);
      }
    } catch (err: any) {
      toast.error(err.message || "File upload failed");
    } finally {
      setUploadingDoc(null);
    }
  };

  // Submit/Save application
  const handleSave = async (isSubmit: boolean) => {
    setLoading(true);
    try {
      const payload = {
        quarterType,
        employeeDetails,
        documents,
        status: (isSubmit ? "submitted" : "draft") as "submitted" | "draft",
      };

      let res;
      if (applicationId) {
        res = await quartersService.updateApplication(applicationId, payload);
      } else {
        res = await quartersService.createApplication(payload);
      }

      if (res.success) {
        toast.success(isSubmit ? "Application submitted successfully!" : "Draft saved successfully");
        navigate("/quarters");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save application");
    } finally {
      setLoading(false);
    }
  };

  // Calculate local score preview
  const getScorePreview = () => {
    let score = 0;
    const items = [];

    for (const w of weights) {
      if (w.isDeleted) continue;

      let val = w.fieldKey.split(".").reduce((acc: any, part: string) => acc && acc[part], {
        quarterType,
        employeeDetails,
      });

      if (
        w.fieldKey === "employeeDetails.joiningDate" ||
        w.fieldKey === "employeeDetails.instituteJoiningDate" ||
        w.fieldKey === "employeeDetails.dob"
      ) {
        if (val) {
          const diffYears = (new Date().getTime() - new Date(val).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          val = Math.max(0, parseFloat(diffYears.toFixed(2)));
        } else {
          val = 0;
        }
      }

      let subScore = 0;
      if (w.fieldType === "boolean") {
        subScore = val === true || val === "true" ? w.weight : 0;
      } else if (w.fieldType === "numeric") {
        subScore = !isNaN(parseFloat(val)) ? parseFloat(val) * w.weight : 0;
      } else if (w.fieldType === "option" && val) {
        const match = w.optionValues?.find((o: any) => o.optionText.toLowerCase() === String(val).toLowerCase());
        subScore = match ? match.value : 0;
      }

      subScore = Math.round(subScore * 100) / 100;
      score += subScore;
      if (subScore > 0) {
        items.push({ name: w.fieldName, value: subScore });
      }
    }
    return { score: Math.round(score * 100) / 100, items };
  };

  const preview = getScorePreview();

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Portal
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-blue-600" /> Accommodation Application Wizard
          </h1>
        </div>
      </div>

      {/* Steps Indicator */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 mb-8 shadow-sm flex justify-around items-center max-w-xl mx-auto">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition ${
                step === s
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                  : step > s
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span
              className={`text-xs font-bold ${
                step === s ? "text-blue-600" : step > s ? "text-emerald-600" : "text-slate-400"
              }`}
            >
              {s === 1 ? "Personal" : s === 2 ? "Service" : s === 3 ? "Documents" : "Review"}
            </span>
          </div>
        ))}
      </div>

      {/* Main Form content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form panel */}
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-6">
          {/* STEP 1: Personal Details */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-slate-800 font-serif">1. Personal Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Employee ID*</label>
                  <input
                    type="text"
                    required
                    value={employeeDetails.employeeId}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, employeeId: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="e.g. EMP001"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Gender*</label>
                  <select
                    value={employeeDetails.gender}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, gender: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Date of Birth*</label>
                  <input
                    type="date"
                    required
                    value={employeeDetails.dob}
                    onChange={(e) => handleDobChange(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Computed Age (Years)</label>
                  <input
                    type="number"
                    readOnly
                    value={employeeDetails.age}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-100 font-bold text-slate-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Marital Status</label>
                  <select
                    value={employeeDetails.maritalStatus}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, maritalStatus: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Divorced">Divorced</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Dependents Count</label>
                  <input
                    type="number"
                    value={employeeDetails.dependentsCount}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, dependentsCount: parseInt(e.target.value) || 0 })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    min={0}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Children Count</label>
                  <input
                    type="number"
                    value={employeeDetails.childrenCount}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, childrenCount: parseInt(e.target.value) || 0 })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    min={0}
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Service Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-slate-800 font-serif">2. Service & Employment</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Department*</label>
                  <input
                    type="text"
                    required
                    value={employeeDetails.department}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, department: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="e.g. Cardiology"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Designation*</label>
                  <input
                    type="text"
                    required
                    value={employeeDetails.designation}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, designation: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="e.g. Professor, Registrar"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Cadre</label>
                  <select
                    value={employeeDetails.cadre}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, cadre: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="Faculty">Faculty</option>
                    <option value="Non-Faculty">Non-Faculty</option>
                    <option value="Administration">Administration</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Employment Type</label>
                  <select
                    value={employeeDetails.employmentType}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, employmentType: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="Permanent">Permanent</option>
                    <option value="Contract">Contract</option>
                    <option value="Ad-hoc">Ad-hoc</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Service Joining Date*</label>
                  <input
                    type="date"
                    required
                    value={employeeDetails.joiningDate}
                    onChange={(e) => handleJoiningDateChange(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Institute Joining Date</label>
                  <input
                    type="date"
                    value={employeeDetails.instituteJoiningDate}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, instituteJoiningDate: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Scheduled Retirement</label>
                  <input
                    type="date"
                    value={employeeDetails.retirementDate}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, retirementDate: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Current Residence Location</label>
                  <input
                    type="text"
                    value={employeeDetails.currentResidence}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, currentResidence: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="e.g. Rented apartment (Out of Campus)"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Current Allotted Quarter (If any)</label>
                  <input
                    type="text"
                    value={employeeDetails.currentQuarter}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, currentQuarter: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="e.g. Flat A-202 (If upgrading)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Uploads & Verification */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-slate-800 font-serif">3. Documents & Medical</h2>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={employeeDetails.isPwd}
                      onChange={(e) => setEmployeeDetails({ ...employeeDetails, isPwd: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Applicant belongs to PWD Category
                  </label>
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeDetails.customFields?.isWidow)}
                      onChange={(e) =>
                        setEmployeeDetails({
                          ...employeeDetails,
                          customFields: { ...(employeeDetails.customFields ?? {}), isWidow: e.target.checked },
                        })
                      }
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Widow / Single-Parent Status
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Emergency Category</label>
                  <select
                    value={employeeDetails.emergencyCategory}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, emergencyCategory: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="None">None</option>
                    <option value="Medical Emergency">Medical Emergency</option>
                    <option value="Security threat">Security Threat</option>
                    <option value="Official Requirement">Official (Dean/Director/etc.)</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Medical Conditions (Specify)</label>
                  <input
                    type="text"
                    value={employeeDetails.medicalConditions}
                    onChange={(e) => setEmployeeDetails({ ...employeeDetails, medicalConditions: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="Specify conditions..."
                  />
                </div>
              </div>

              {/* Upload Interface */}
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <h4 className="text-slate-800 font-bold text-sm mb-3">Attach Supporting Documents (PDF/Images)</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Joining Letter */}
                  <div className="p-4 border border-slate-100 rounded-2xl bg-slate-50/50 space-y-3">
                    <p className="text-xs font-bold text-slate-700">Service Joining Letter*</p>
                    <div className="flex items-center justify-between gap-2">
                      {documents.some((d) => d.name === "joining_letter") ? (
                        <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Uploaded
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No file attached</span>
                      )}
                      <label className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg cursor-pointer transition flex items-center gap-1 shadow-sm">
                        <Upload className="w-3.5 h-3.5" />
                        {uploadingDoc === "joining_letter" ? "Uploading..." : "Choose File"}
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, "joining_letter")}
                          disabled={uploadingDoc !== null}
                        />
                      </label>
                    </div>
                  </div>

                  {/* PWD Certificate (Conditional) */}
                  {employeeDetails.isPwd && (
                    <div className="p-4 border border-slate-100 rounded-2xl bg-slate-50/50 space-y-3">
                      <p className="text-xs font-bold text-slate-700">PWD Disability Certificate*</p>
                      <div className="flex items-center justify-between gap-2">
                        {documents.some((d) => d.name === "pwd_cert") ? (
                          <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Uploaded
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No file attached</span>
                        )}
                        <label className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg cursor-pointer transition flex items-center gap-1 shadow-sm">
                          <Upload className="w-3.5 h-3.5" />
                          {uploadingDoc === "pwd_cert" ? "Uploading..." : "Choose File"}
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, "pwd_cert")}
                            disabled={uploadingDoc !== null}
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Medical Reports */}
                  {employeeDetails.medicalConditions && (
                    <div className="p-4 border border-slate-100 rounded-2xl bg-slate-50/50 space-y-3">
                      <p className="text-xs font-bold text-slate-700">Medical Fitness Certificate / Case File</p>
                      <div className="flex items-center justify-between gap-2">
                        {documents.some((d) => d.name === "medical_file") ? (
                          <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Uploaded
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No file attached</span>
                        )}
                        <label className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg cursor-pointer transition flex items-center gap-1 shadow-sm">
                          <Upload className="w-3.5 h-3.5" />
                          {uploadingDoc === "medical_file" ? "Uploading..." : "Choose File"}
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, "medical_file")}
                            disabled={uploadingDoc !== null}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Review and Submit */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-slate-800 font-serif">4. Preferred Quarter & Review</h2>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase">Preferred Quarter Type*</label>
                <select
                  value={quarterType}
                  onChange={(e) => setQuarterType(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition font-bold"
                >
                  <option value="Type I">Type I (Lower grade staff)</option>
                  <option value="Type II">Type II (Staff nurses / Technical staff)</option>
                  <option value="Type III">Type III (Junior officers / Assistant professors)</option>
                  <option value="Type IV">Type IV (Associate professors / senior administrative officers)</option>
                  <option value="Type V">Type V (Professors / Directors / Deans)</option>
                </select>
              </div>

              {/* Review summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs bg-slate-50 p-6 border border-slate-100 rounded-2xl">
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Personal Summary</p>
                  <p className="font-bold text-slate-800 mt-1">ID: {employeeDetails.employeeId}</p>
                  <p className="text-slate-600">
                    Gender: {employeeDetails.gender} | Age: {employeeDetails.age} Yrs
                  </p>
                  <p className="text-slate-600">Children: {employeeDetails.childrenCount}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Service Summary</p>
                  <p className="font-bold text-slate-800 mt-1">
                    {employeeDetails.designation} ({employeeDetails.department})
                  </p>
                  <p className="text-slate-600">Joining Date: {employeeDetails.joiningDate}</p>
                  <p className="text-slate-600">Service duration: {employeeDetails.serviceYears} Yrs</p>
                </div>
              </div>

              {/* Declaration checkbox */}
              <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed pt-4 border-t border-slate-100">
                <input type="checkbox" required className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5" />
                I hereby declare that the particulars furnished above are true to the best of my knowledge and belief. I agree to comply with the institute quarters allotment rules.
              </label>
            </div>
          )}

          {/* Bottom Navigation */}
          <div className="flex justify-between border-t border-slate-100 pt-6 mt-8">
            <button
              type="button"
              disabled={step === 1}
              onClick={() => setStep(step - 1)}
              className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-30"
            >
              Previous
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={loading}
                className="bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm px-5 py-2.5 rounded-xl border border-slate-200 transition flex items-center gap-1 shadow-sm"
              >
                <Save className="w-4 h-4" /> Save Draft
              </button>
              {step < 4 ? (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition shadow-md shadow-blue-200 flex items-center gap-1"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSave(true)}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition shadow-md shadow-emerald-200 flex items-center gap-1"
                >
                  Submit Application
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Scoring Engine Preview Panel */}
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white border border-slate-800 p-6 rounded-3xl shadow-xl flex flex-col justify-between">
            <div>
              <span className="bg-white/10 text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">
                Seniority Score Preview
              </span>
              <p className="text-[10px] text-white/50 mt-1 font-semibold">
                Estimations based on active weights configuration
              </p>
            </div>

            <div className="py-8 text-center">
              <p className="text-6xl font-black tracking-tight text-indigo-300 font-mono">{preview.score}</p>
              <p className="text-xs text-white/60 font-bold uppercase tracking-wide mt-2">Calculated Total Score</p>
            </div>

            <div className="border-t border-white/10 pt-4 space-y-2.5 max-h-[220px] overflow-y-auto pr-1">
              <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mb-2">Score Breakdown</p>
              {preview.items.length > 0 ? (
                preview.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center text-xs font-medium">
                    <span className="text-white/70">{item.name}</span>
                    <span className="text-indigo-300 font-bold">+{item.value}</span>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-xs italic">Enter details to calculate score breakdown.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
